import { createClient as createSupabaseClient, SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Singleton pattern to prevent multiple client instances
let supabaseClient: SupabaseClient | null = null

export function createClient(): SupabaseClient {
  // Return existing client if it exists
  if (supabaseClient) {
    return supabaseClient
  }

  // Create new client only once
  supabaseClient = createSupabaseClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'sb-auth-token', // Use consistent storage key
    },
  })

  return supabaseClient
}

/**
 * Retrieve the active API key for a user, checking the api_keys table first
 * and falling back to the profiles table.
 */
export async function getUserApiKey(supabase: SupabaseClient, userId: string): Promise<string | null> {
  try {
    // 1. Check the new api_keys table for the most recent key
    const { data: keys, error: keysError } = await supabase
      .from('api_keys')
      .select('api_key')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)

    if (!keysError && keys && keys.length > 0) {
      return keys[0].api_key
    }

    // 2. Fallback to legacy profiles table api_key column
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('api_key')
      .eq('id', userId)
      .maybeSingle()

    if (!profileError && profile?.api_key) {
      return profile.api_key
    }

    return null
  } catch (error) {
    console.error('Error fetching user API key:', error)
    return null
  }
}

/**
 * Ensures the user has a profile and at least one API key.
 * This is a self-healing function to mitigate failures in signup database triggers.
 */
export async function ensureUserProfileAndKey(
  supabase: SupabaseClient,
  userId: string,
  email: string,
  name?: string
): Promise<void> {
  try {
    // 1. Check if profile exists
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .maybeSingle()

    // 2. If profile is missing, create it
    if (!profileError && !profile) {
      console.log('Profile missing for user. Creating profile manually...', userId)
      const defaultName = name || email.split('@')[0] || 'User'
      
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          name: defaultName,
          subscription_tier: 'free'
        })

      if (insertError) {
        console.error('Failed to create user profile manually:', insertError)
      }
    }

    // 3. Check if at least one API key exists in the api_keys table
    const { data: keys, error: keysError } = await supabase
      .from('api_keys')
      .select('id')
      .eq('user_id', userId)
      .limit(1)

    // 4. If no keys are present, generate and insert a default API key
    if (!keysError && (!keys || keys.length === 0)) {
      console.log('Default API key missing for user. Creating one manually...', userId)
      const newKey = 'atg_' + Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')

      const { error: insertKeyError } = await supabase
        .from('api_keys')
        .insert({
          user_id: userId,
          api_key: newKey,
          name: 'Default API Key'
        })

      if (insertKeyError) {
        console.error('Failed to create default API key manually:', insertKeyError)
      }
    }
  } catch (error) {
    console.error('Error ensuring user profile and keys:', error)
  }
}

